import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { queryUsers } from '@/features/users/data';
import { useAccessToken } from '@/shared/hooks/auth';
import { queryKeys } from '@/shared/hooks/queryKeys';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import type { OrganisationRole } from '@flowqa/shared';

export const useCurrentOrgRole = (
  userId: string | null | undefined,
  options?: { enabled?: boolean },
): { role: OrganisationRole | null; isAdmin: boolean; isLoading: boolean } => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const query = useQuery({
    queryKey: queryKeys.currentUserRole(userId, selectedOrganisationId ?? null),
    enabled: Boolean(token && userId && selectedOrganisationId) && (options?.enabled ?? true),
    queryFn: async () =>
      await queryUsers(
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'email', direction: 'asc' }],
          filters: buildEqFilter('id', userId),
        },
        token,
      ),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const role = useMemo<OrganisationRole | null>(() => {
    const items = query.data?.items;
    if (!items || items.length === 0) return null;
    return items[0].role ?? null;
  }, [query.data?.items]);

  return { role, isAdmin: role === 'ADMIN', isLoading: query.isLoading };
};
